import React from 'react';
import { useTranslation } from 'react-i18next';

import { Panel, SectionTitle, Button, Callout, Field, Icon, Text, Skeleton } from '../../../../components';
import { CASINO_SETTINGS, COLORS } from '../../../../theme/tokens';
import {
  addSearchConsoleRecord,
  getSearchConsole,
  normaliseVerificationValue,
  searchConsoleConfigured,
  SearchConsoleError,
  type SearchConsoleRecord,
  type SearchConsoleStatus,
} from '../../search-console-api';
import { SectionSubtitle } from '../shared.styles';
import { Card, StatusTag } from '../DomainsSection/DomainsSection.styles';
import {
  TitleRow,
  GoogleMark,
  Stack,
  Steps,
  Step,
  StepBody,
  StepText,
  OutlineLink,
  Form,
  FormRow,
  SubmitSlot,
  SubmitSpinner,
  NoticeSlot,
  ErrorText,
  MutedNote,
  RecordsHead,
  RecordsTitle,
  RecordList,
  RecordRow,
  RecordValue,
} from './SearchConsoleSection.styles';

export type SearchConsoleSectionProps = {
  /** The active brand's tenant id; undefined (no brand) hides the panel. */
  tenantId: number | undefined;
  /** The page's toast. */
  onToast: (message: string, placeholders?: Record<string, string | number>) => void;
};

const SEARCH_CONSOLE_URL = 'https://search.google.com/search-console/welcome';

/** What the inline line under the input says. */
type Notice = 'invalid' | 'wrong_method' | 'too_many' | 'failed';

/** Shown when the status read itself failed before any answer arrived for this brand. */
const UNAVAILABLE: SearchConsoleStatus = {
  domain: '',
  eligible: false,
  reason: 'unavailable',
  records: [],
  max_records: 10,
};

/** The panel stays hidden: the service has no Search Console route yet (it answered 404), like a brand with no
 *  domain of its own. */
const HIDDEN: SearchConsoleStatus = { ...UNAVAILABLE, reason: 'no_domain' };

/** "Open Search Console", the outline link-button used in both the eligible and the own-registrar states. */
const OpenSearchConsole = (): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <OutlineLink
      href={SEARCH_CONSOLE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('Open {{name}} in a new tab', { name: t('Google Search Console') })}
    >
      <Text text="Open Search Console" tag="span" />
      <Icon name="external-link" size={16} color={COLORS.ctaText} />
    </OutlineLink>
  );
};

/** One verification record: the full value on a single ellipsised line, with its DNS state. */
const RecordLine = ({ record }: { record: SearchConsoleRecord }): React.ReactElement => (
  <RecordRow>
    <RecordValue text={record.value} tag="span" title={record.value} raw />
    {record.live ? (
      <StatusTag $bg={CASINO_SETTINGS.okBg} $fg={CASINO_SETTINGS.okFg}>
        <Icon name="check" size={12} color={CASINO_SETTINGS.okFg} />
        <Text text="Live" tag="span" />
      </StatusTag>
    ) : (
      <StatusTag $bg={CASINO_SETTINGS.lockBg} $fg={CASINO_SETTINGS.lockFg}>
        <Icon name="clock" size={12} color={CASINO_SETTINGS.lockFg} />
        <Text text="Waiting for DNS" tag="span" />
      </StatusTag>
    )}
  </RecordRow>
);

/**
 * Google Search Console: lets the owner prove to Google that they own the brand's domain. For a domain we bought
 * for them, its DNS is ours to write, so they paste the TXT record Google shows for a Domain property and we
 * publish it (spark-creatives, through Lumio Relay). For a domain they brought themselves the panel only explains
 * where to add the record. Hidden when the creatives service is not configured, there is no brand, or the brand
 * has no public domain of its own.
 */
/** The panel's heading with Google's "G" in place of a generic icon. */
const PanelTitle = ({ title }: { title: string }): React.ReactElement => (
  <TitleRow>
    <GoogleMark provider="google" />
    <SectionTitle title={title} />
  </TitleRow>
);

export const SearchConsoleSection = ({ tenantId, onToast }: SearchConsoleSectionProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const configured = searchConsoleConfigured();

  /** null = the first read for this brand has not answered yet. */
  const [status, setStatus] = React.useState<SearchConsoleStatus | null>(null);
  /** A re-read ("Check again" / "Try again") is in flight. */
  const [checking, setChecking] = React.useState(false);
  const [value, setValue] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [notice, setNotice] = React.useState<Notice | null>(null);

  // Every read gets an id from a growing counter and only the newest one lands. A brand switch or a fresher
  // answer (the POST's) disowns whatever is still in flight.
  const readSeq = React.useRef(0);
  const readAbort = React.useRef<AbortController | null>(null);
  // Bumped on every brand change, so an add that answers after a switch is dropped.
  const brandEpoch = React.useRef(0);

  const disownReads = React.useCallback(() => {
    readSeq.current += 1;
    readAbort.current?.abort();
    readAbort.current = null;
    setChecking(false);
  }, []);

  const read = React.useCallback((id: number) => {
    readSeq.current += 1;
    const seq = readSeq.current;
    readAbort.current?.abort();
    const controller = new AbortController();
    readAbort.current = controller;
    setChecking(true);
    getSearchConsole(id, controller.signal)
      .then((next) => {
        // An answer that says the service could not check is a failed read too: a re-read keeps the last real
        // answer (not the client's own placeholder, which this answer improves on by naming the domain).
        if (seq === readSeq.current) {
          setStatus((prev) => (prev && prev !== UNAVAILABLE && next.reason === 'unavailable' ? prev : next));
        }
      })
      .catch((err: unknown) => {
        if (seq !== readSeq.current) return;
        // A service without the route yet: no panel rather than a warning. Only while nothing real is held: a 404
        // after the route has answered is a passing failure (a mixed-version rollout) and keeps the last answer.
        if (err instanceof SearchConsoleError && err.status === 404) {
          setStatus((prev) => (prev === null || prev === UNAVAILABLE ? HIDDEN : prev));
          return;
        }
        // A failed re-read keeps the last answer; only a first read that fails shows the "couldn't check" state.
        setStatus((prev) => prev ?? UNAVAILABLE);
      })
      .finally(() => {
        if (seq === readSeq.current) {
          readAbort.current = null;
          setChecking(false);
        }
      });
  }, []);

  React.useEffect(() => {
    brandEpoch.current += 1;
    disownReads();
    setStatus(null);
    setValue('');
    setNotice(null);
    setSubmitting(false);
    if (configured && tenantId !== undefined) read(tenantId);
  }, [configured, tenantId, read, disownReads]);

  // Unmount: nothing in flight may land, neither a read nor an add.
  React.useEffect(
    () => () => {
      brandEpoch.current += 1;
      disownReads();
    },
    [disownReads],
  );

  const recheck = React.useCallback(() => {
    if (tenantId !== undefined) read(tenantId);
  }, [read, tenantId]);

  const onChange = React.useCallback((next: string) => {
    setValue(next);
    setNotice((prev) => (prev === 'too_many' ? prev : null));
  }, []);

  const atMax = status !== null && status.records.length >= status.max_records;

  const onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (tenantId === undefined || status === null || !status.eligible || submitting || atMax || !value.trim()) return;
    const submitted = value;
    const checked = normaliseVerificationValue(value);
    if (!checked.ok) {
      setNotice(checked.code === 'search_console_wrong_method' ? 'wrong_method' : 'invalid');
      return;
    }
    const epoch = brandEpoch.current;
    const domain = status.domain;
    setSubmitting(true);
    setNotice(null);
    addSearchConsoleRecord(tenantId, checked.value)
      .then((next) => {
        if (epoch !== brandEpoch.current) return;
        disownReads();
        setStatus(next);
        // Clear the field only if the owner has not typed something new while the record was being added.
        setValue((current) => (current === submitted ? '' : current));
        onToast(t('Verification record added to {{domain}}'), { domain: next.domain || domain });
      })
      .catch((err: unknown) => {
        if (epoch !== brandEpoch.current) return;
        const code = err instanceof SearchConsoleError ? err.code : '';
        if (code === 'search_console_invalid_token') setNotice('invalid');
        else if (code === 'search_console_wrong_method') setNotice('wrong_method');
        else if (code === 'search_console_too_many') {
          // The zone filled up elsewhere: re-read so the list and the cap match it.
          setNotice('too_many');
          read(tenantId);
        } else if (code === 'search_console_not_managed' || code === 'search_console_no_domain') {
          // The domain changed hands or lost its DNS: say the add failed, and re-read so the panel re-renders in the
          // right state (the form, and with it the notice, goes away when it does).
          setNotice('failed');
          read(tenantId);
        } else setNotice('failed');
      })
      .finally(() => {
        if (epoch === brandEpoch.current) setSubmitting(false);
      });
  };

  if (!configured || tenantId === undefined) return null;

  const title = t('Google Search Console');

  if (status === null) {
    return (
      <Panel>
        <PanelTitle title={title} />
        <Skeleton block height={180} radius={14} />
      </Panel>
    );
  }

  if (status.reason === 'no_domain') return null;

  const domain = { domain: status.domain };

  if (status.reason === 'unavailable' || (!status.eligible && status.reason !== 'not_managed')) {
    const retry = <Button label={t('Try again')} variant="outline" onClick={recheck} disabled={checking} />;
    return (
      <Panel>
        <PanelTitle title={title} />
        {status.domain ? (
          <Callout tone="warning" text="We couldn't check {{domain}} right now." placeholders={domain} action={retry} />
        ) : (
          <Callout tone="warning" text="We couldn't check your domain right now." action={retry} />
        )}
      </Panel>
    );
  }

  if (status.reason === 'not_managed') {
    const live = status.records.filter((record) => record.live);
    return (
      <Panel>
        <PanelTitle title={title} />
        <Stack>
          <Callout
            tone="info"
            text="{{domain}} is connected through your own registrar. Add the TXT record Google gives you in your registrar's DNS settings."
            placeholders={domain}
            action={<OpenSearchConsole />}
          />
          {live.length > 0 ? (
            <Card>
              <RecordsTitle text="Verification records" tag="span" />
              <RecordList>
                {live.map((record) => (
                  <RecordLine key={record.value} record={record} />
                ))}
              </RecordList>
            </Card>
          ) : null}
        </Stack>
      </Panel>
    );
  }

  const addDisabled = atMax || submitting || !value.trim();

  return (
    <Panel>
      <PanelTitle title={title} />
      <SectionSubtitle
        text="Verify that you own {{domain}} in Google Search Console to see how your site performs in Google Search."
        placeholders={domain}
        tag="div"
      />
      <Stack>
        <Card>
          <Steps>
            <Step>
              <StepBody>
                <StepText
                  text='In Search Console, add a Domain property for {{domain}} and choose "Any DNS provider".'
                  placeholders={domain}
                  tag="span"
                />
                <OpenSearchConsole />
              </StepBody>
            </Step>
            <Step>
              <StepBody>
                <StepText text="Copy the TXT record Google shows you and paste it here." tag="span" />
                <Form onSubmit={onSubmit} noValidate>
                  <FormRow>
                    <Field
                      label={t('Google verification record')}
                      value={value}
                      onChange={onChange}
                      placeholder={t('google-site-verification=…')}
                      disabled={atMax}
                    />
                    <SubmitSlot $busy={submitting}>
                      <Button label={t('Add to DNS')} type="submit" disabled={addDisabled} />
                      {submitting ? <SubmitSpinner role="status" aria-label={t('Adding the record…')} /> : null}
                    </SubmitSlot>
                  </FormRow>
                  <NoticeSlot aria-live="polite">
                    {atMax || notice === 'too_many' ? (
                      <MutedNote
                        text="This domain already has the maximum number of Google verification records."
                        tag="span"
                      />
                    ) : notice === 'invalid' ? (
                      <ErrorText
                        text="That doesn't look like a Google verification record. Copy the whole value that starts with google-site-verification=."
                        tag="span"
                      />
                    ) : notice === 'wrong_method' ? (
                      <ErrorText
                        text="That's for the HTML file or meta tag method. In Search Console, add a Domain property and copy its TXT record instead."
                        tag="span"
                      />
                    ) : notice === 'failed' ? (
                      <ErrorText text="We couldn't add the record right now. Please try again." tag="span" />
                    ) : null}
                  </NoticeSlot>
                </Form>
              </StepBody>
            </Step>
            <Step>
              <StepBody>
                <StepText
                  text="Then click Verify in Search Console. Google can take a few minutes, sometimes up to a day, to see the record."
                  tag="span"
                />
              </StepBody>
            </Step>
          </Steps>
        </Card>

        {status.records.length > 0 ? (
          <Card>
            <RecordsHead>
              <RecordsTitle text="Verification records" tag="span" />
              <Button label={t('Check again')} variant="text" icon="refresh" onClick={recheck} disabled={checking} />
            </RecordsHead>
            <RecordList>
              {status.records.map((record) => (
                <RecordLine key={record.value} record={record} />
              ))}
            </RecordList>
            <MutedNote
              text="Records added here stay on your domain. Contact support if you need one removed."
              tag="span"
            />
          </Card>
        ) : null}
      </Stack>
    </Panel>
  );
};

export default SearchConsoleSection;
